import {Box, HBox} from '../../../../../src/components/core/view';
import {
  H5Text,
  H6Text,
  PSmallText,
} from '../../../../../src/components/core/text';
import {Linking, StyleSheet} from 'react-native';
import React, {useCallback, useMemo, useState} from 'react';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../../src/appStyles/hooks/theme';

import {Analytics} from '../../../../../src/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import Directions from '@buncha/components/core/icon/Directions';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import LocationIcon from '../../../../../src/components/core/icon/LocationIcon';
import {MapChoice} from '../../../../../src/onBoarding/types';
import {Modules} from '@buncha/utils/types';
import {MostRecentAddress} from '../../../../../src/driver/types/common';
import {Task} from '@buncha/config/onfleet/types';
import {Touchable} from '../../../../../src/components/core/touchable';
import {getMapUrl} from '../../../../../src/utils/common';
import listener from '@buncha/config/listener';
import onfleetService from '@buncha/config/onfleet';
import {splitAtFirstComma} from '@buncha/driver/shared/util';
import {useAppSelector} from '../../../../../src/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';

interface BuyerAddressCellProps {
  buyerAddress: MostRecentAddress | undefined;
  currentTask: Task | undefined;
}

const BuyerAddressCell: React.FC<BuyerAddressCellProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const buyerAddress = props?.buyerAddress;
  const task = props?.currentTask;
  const lat =
    task?.destinationAddress?.locationLatitude || buyerAddress?.lat || 0;
  const lon =
    task?.destinationAddress?.locationLongitude || buyerAddress?.lon || 0;
  const user = useAppSelector(gstate => gstate.onboarding.currentUser);
  const mapChoice = user?.mapChoice || MapChoice.googleMaps;
  const [navigateModal, setNavigateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const {body, button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const address = buyerAddress?.address || '';
  const onfleetAddress = `${task?.destinationAddress?.address} ${task?.destinationAddress?.city} ${task?.destinationAddress?.postalCode}`;

  const onfleetAddress1 = `${task?.destinationAddress?.address}`;
  const onfleetAddress2 = `${task?.destinationAddress?.city}, ${task?.destinationAddress?.state} ${task?.destinationAddress?.postalCode}`;

  const isAddressCorrect = useMemo(() => {
    if (!address || !onfleetAddress) return false;

    return address.split(',').join('') === onfleetAddress;
  }, [address, onfleetAddress]);

  const [address1, address2] = splitAtFirstComma(address);

  const showOnfleetAddress = !address1 && !address2;

  const toggleNavigateModal = useCallback(() => setNavigateModal(s => !s), []);

  const openUrl = useCallback(() => {
    setNavigateModal(false);
    const coords = {
      lat: lat || 0,
      lng: lon || 0,
    };
    Analytics.eventWithProps('Open map for driver', {
      mapChoice: mapChoice,
      buyerLat: lat,
      buyerLon: lon,
      type: 'CTA',
      userType: 'driver',
    });
    Linking.openURL(getMapUrl(mapChoice, coords, address1));
  }, [address1, lat, lon, mapChoice]);

  const changeStatusToOnTheWay = useCallback(async () => {
    if (!task?.id) return;
    setLoading(true);
    const res = await onfleetService.sendOnTheWay(task.id);
    setLoading(false);
    if (res) listener.emit('onTheWay');
    openUrl();
  }, [openUrl, task?.id]);

  return (
    <HBox style={styles.mainContainer}>
      <HBox style={styles.parentBox}>
        <LocationIcon viewBox="-3 0 20 20" fill={theme?.icon?.blue?.[300]} />
        <Box>
          <If condition={task}>
            <If condition={address1 || onfleetAddress1}>
              <H5Text
                alignText="left"
                numberOfLines={!address2 ? undefined : 1}
                style={[
                  styles.location,
                  {
                    color: theme?.text?.blue?.[300],
                    fontFamily: muli?.[700]?.normal,
                  },
                ]}>
                {showOnfleetAddress ? onfleetAddress1 : address1}
              </H5Text>
            </If>
            <If condition={address2 || onfleetAddress2}>
              <H6Text
                alignText="left"
                numberOfLines={1}
                style={[
                  styles.location,
                  {
                    color: theme?.text?.gray?.[100],
                    fontFamily: muli?.[600]?.normal,
                  },
                ]}>
                {showOnfleetAddress ? onfleetAddress2 : address2}
              </H6Text>
            </If>
            <If condition={!isAddressCorrect}>
              <PSmallText
                style={[
                  styles.textAlignment,
                  {
                    color: theme?.text?.red?.[100],
                    fontFamily: muli?.[600]?.normal,
                  },
                ]}>
                {body?.addressMismatch}
              </PSmallText>
            </If>
          </If>
          <If condition={!task}>
            <H5Text
              alignText="left"
              numberOfLines={2}
              style={[
                styles.location,
                {
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[600]?.normal,
                },
              ]}>
              {address}
            </H5Text>
          </If>
        </Box>
      </HBox>
      <If condition={Boolean(lat && lon)}>
        <Touchable
          onPress={toggleNavigateModal}
          style={{backgroundColor: theme?.background?.green?.[100]}}>
          <Directions width={40} height={40} fill={theme?.icon?.white?.[100]} />
        </Touchable>
      </If>
      <AppModal
        isOpen={navigateModal}
        onClose={toggleNavigateModal}
        disableCtaTop={loading}
        topCTAHandelr={changeStatusToOnTheWay}
        title={task?.state ? body?.navigateToLocation : body?.taskNotStarted}
        // eslint-disable-next-line react-native/no-inline-styles
        titleStyle={{
          fontFamily: muli?.[600]?.normal,
          lineHeight: 25,
        }}
        ctaTopTitle={button?.startNavigate}
        ctaBottomTitle={button?.navigate}
        topCTAColor={theme?.text?.green?.[100]}
        bottomCTAHandelr={openUrl}
        hideTopCta={!task?.id || Boolean(task?.state)}
      />
    </HBox>
  );
};

export default React.memo(BuyerAddressCell);

const styles = StyleSheet.create({
  location: {
    flex: 1,
    padding: 0,
    marginLeft: 10,
  },
  textAlignment: {
    textAlign: 'left',
    marginLeft: 10,
  },
  parentBox: {
    marginBottom: 10,
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 0.9,
  },
  touchable: {
    display: 'flex',
    flexDirection: 'row',
  },
  mainContainer: {
    flex: 1,
  },
});
